import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Clock,
  Calendar,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  MapPin,
  FileText,
  Settings,
  Award,
  UserCheck,
  UserX,
  Timer,
  Briefcase
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HRDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    total_employees: 0,
    present_today: 0,
    late_today: 0,
    absent_today: 0,
    pending_leaves: 0,
    payroll_processed: 0,
    pending_reviews: 0,
    active_advances: 0
  });
  const [todayAttendance, setTodayAttendance] = useState({
    date: '',
    is_holiday: false,
    holiday_name: null,
    records: [],
    leaves: []
  });
  const [performanceData, setPerformanceData] = useState({
    top_performers: [],
    needs_improvement: []
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, attendanceRes, performanceRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/hr/dashboard-summary`, { headers }),
        axios.get(`${API_BASE_URL}/hr/attendance/today`, { headers }),
        axios.get(`${API_BASE_URL}/hr/performance/dashboard`, { headers })
      ]);

      setDashboardData(summaryRes.data);
      setTodayAttendance(attendanceRes.data);
      setPerformanceData(performanceRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      className="cursor-pointer"
      onClick={onClick}
    >
      <Card className="bg-wt-surface border-wt-border hover:border-wt-gold/30 transition-colors">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-wt-text-muted font-medium">{title}</p>
              <p className={`text-3xl font-bold mt-1 ${color || 'text-wt-text-primary'}`}>
                {value}
              </p>
              {subtitle && (
                <p className="text-xs text-wt-text-muted mt-1">{subtitle}</p>
              )}
            </div>
            <div className={`p-3 rounded-xl ${color ? 'bg-opacity-10' : 'bg-wt-gold/10'}`}>
              <Icon className={`h-6 w-6 ${color || 'text-wt-gold'}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  const AttendanceStatusBadge = ({ status }) => {
    const statusConfig = {
      on_time: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'On Time' },
      late: { color: 'bg-yellow-500/20 text-yellow-400', icon: Timer, label: 'Late' },
      half_day: { color: 'bg-orange-500/20 text-orange-400', icon: Clock, label: 'Half Day' },
      absent: { color: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Absent' },
      on_leave: { color: 'bg-blue-500/20 text-blue-400', icon: Calendar, label: 'On Leave' }
    };

    const config = statusConfig[status] || statusConfig.absent;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">HR Dashboard</h1>
          <p className="text-wt-text-muted">
            {todayAttendance.is_holiday 
              ? `Today is ${todayAttendance.holiday_name}` 
              : `Today: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/hr/attendance')}
            className="border-wt-border text-wt-text-secondary hover:text-wt-gold"
            data-testid="view-attendance-btn"
          >
            <Clock className="h-4 w-4 mr-2" />
            Attendance
          </Button>
          <Button
            onClick={() => navigate('/hr/employees')}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            data-testid="manage-employees-btn"
          >
            <Users className="h-4 w-4 mr-2" />
            Employees
          </Button>
        </div>
      </div>

      {/* Holiday Banner */}
      {todayAttendance.is_holiday && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center gap-3"
        >
          <Calendar className="h-6 w-6 text-blue-400" />
          <div>
            <p className="text-blue-400 font-medium">Holiday Today</p>
            <p className="text-sm text-wt-text-muted">{todayAttendance.holiday_name}</p>
          </div>
        </motion.div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Employees"
          value={dashboardData.total_employees}
          icon={Users}
          onClick={() => navigate('/hr/employees')}
        />
        <StatCard
          title="Present Today"
          value={dashboardData.present_today}
          icon={UserCheck}
          color="text-green-400"
          subtitle={`${Math.round((dashboardData.present_today / Math.max(dashboardData.total_employees, 1)) * 100)}% attendance`}
          onClick={() => navigate('/hr/attendance?status=present')}
        />
        <StatCard
          title="Late Today"
          value={dashboardData.late_today}
          icon={Timer}
          color="text-yellow-400"
          onClick={() => navigate('/hr/attendance?status=late')}
        />
        <StatCard
          title="Absent Today"
          value={dashboardData.absent_today}
          icon={UserX}
          color="text-red-400"
          onClick={() => navigate('/hr/absent-today')}
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Pending Leaves"
          value={dashboardData.pending_leaves}
          icon={Calendar}
          color="text-blue-400"
          onClick={() => navigate('/hr/leaves')}
        />
        <StatCard
          title="Pending Reviews"
          value={dashboardData.pending_reviews}
          icon={AlertCircle}
          color="text-orange-400"
          onClick={() => navigate('/hr/attendance?tab=reviews')}
        />
        <StatCard
          title="Active Advances"
          value={dashboardData.active_advances}
          icon={DollarSign}
          color="text-purple-400"
          onClick={() => navigate('/hr/payroll')}
        />
        <StatCard
          title="Payroll Processed"
          value={dashboardData.payroll_processed}
          icon={FileText}
          onClick={() => navigate('/hr/payroll')}
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="attendance" className="w-full">
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="attendance" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            Today's Attendance
          </TabsTrigger>
          <TabsTrigger value="performance" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            Performance
          </TabsTrigger>
          <TabsTrigger value="leaves" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            On Leave Today
          </TabsTrigger>
        </TabsList>

        {/* Today's Attendance Tab */}
        <TabsContent value="attendance" className="mt-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-wt-text-primary">Today's Check-ins</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/hr/attendance-map')}
                className="border-wt-border text-wt-text-secondary hover:text-wt-gold"
              >
                <MapPin className="h-4 w-4 mr-2" />
                View Map
              </Button>
            </CardHeader>
            <CardContent>
              {todayAttendance.records.length === 0 ? (
                <div className="text-center py-8 text-wt-text-muted">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No attendance recorded yet today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayAttendance.records.slice(0, 10).map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-wt-gold/20 flex items-center justify-center">
                          <span className="text-wt-gold font-medium">
                            {record.user_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-wt-text-primary">{record.user_name}</p>
                          <p className="text-xs text-wt-text-muted">
                            Check-in: {new Date(record.check_in_time).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <AttendanceStatusBadge status={record.status} />
                        {record.requires_review && (
                          <span className="px-2 py-1 rounded-full text-xs bg-orange-500/20 text-orange-400">
                            Needs Review
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {todayAttendance.records.length > 10 && (
                    <Button
                      variant="ghost"
                      className="w-full text-wt-gold hover:text-wt-gold-hover"
                      onClick={() => navigate('/hr/attendance')}
                    >
                      View All ({todayAttendance.records.length} records)
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Top Performers */}
            <Card className="bg-wt-surface border-wt-border">
              <CardHeader>
                <CardTitle className="text-wt-text-primary flex items-center gap-2">
                  <Award className="h-5 w-5 text-wt-gold" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {performanceData.top_performers.length === 0 ? (
                  <p className="text-center text-wt-text-muted py-4">No data available</p>
                ) : (
                  <div className="space-y-3">
                    {performanceData.top_performers.map((emp, index) => (
                      <div
                        key={emp.user_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                            index === 1 ? 'bg-gray-400/20 text-gray-400' :
                            index === 2 ? 'bg-orange-600/20 text-orange-600' :
                            'bg-wt-gold/20 text-wt-gold'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-wt-text-primary">{emp.user_name}</p>
                            <p className="text-xs text-wt-text-muted">{emp.department || 'No Department'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-400">{emp.performance_score}%</p>
                          <p className="text-xs text-wt-text-muted">
                            {emp.attendance_streak} day streak
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Needs Improvement */}
            <Card className="bg-wt-surface border-wt-border">
              <CardHeader>
                <CardTitle className="text-wt-text-primary flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-400" />
                  Needs Improvement
                </CardTitle>
              </CardHeader>
              <CardContent>
                {performanceData.needs_improvement.length === 0 ? (
                  <p className="text-center text-wt-text-muted py-4">No data available</p>
                ) : (
                  <div className="space-y-3">
                    {performanceData.needs_improvement.map((emp) => (
                      <div
                        key={emp.user_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-red-500/20 flex items-center justify-center">
                            <AlertCircle className="h-4 w-4 text-red-400" />
                          </div>
                          <div>
                            <p className="font-medium text-wt-text-primary">{emp.user_name}</p>
                            <p className="text-xs text-wt-text-muted">{emp.department || 'No Department'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-400">{emp.performance_score}%</p>
                          <p className="text-xs text-wt-text-muted">
                            Attendance: {emp.attendance_score}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* On Leave Tab */}
        <TabsContent value="leaves" className="mt-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary">Employees on Leave Today</CardTitle>
            </CardHeader>
            <CardContent>
              {todayAttendance.leaves.length === 0 ? (
                <div className="text-center py-8 text-wt-text-muted">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No employees on leave today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayAttendance.leaves.map((leave) => (
                    <div
                      key={leave.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-wt-text-primary">{leave.user_name}</p>
                          <p className="text-xs text-wt-text-muted">
                            {leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)} Leave
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-wt-text-secondary">
                          {leave.start_date} - {leave.end_date}
                        </p>
                        <p className="text-xs text-wt-text-muted">
                          {leave.total_days} days
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Actions */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="text-wt-text-primary">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="border-wt-border text-wt-text-secondary hover:text-wt-gold h-auto py-4 flex-col"
              onClick={() => navigate('/hr/leaves')}
            >
              <Calendar className="h-6 w-6 mb-2" />
              <span>Leave Requests</span>
            </Button>
            <Button
              variant="outline"
              className="border-wt-border text-wt-text-secondary hover:text-wt-gold h-auto py-4 flex-col"
              onClick={() => navigate('/hr/payroll')}
            >
              <DollarSign className="h-6 w-6 mb-2" />
              <span>Payroll</span>
            </Button>
            <Button
              variant="outline"
              className="border-wt-border text-wt-text-secondary hover:text-wt-gold h-auto py-4 flex-col"
              onClick={() => navigate('/hr/holidays')}
            >
              <Briefcase className="h-6 w-6 mb-2" />
              <span>Holidays</span>
            </Button>
            <Button
              variant="outline"
              className="border-wt-border text-wt-text-secondary hover:text-wt-gold h-auto py-4 flex-col"
              onClick={() => navigate('/hr/settings')}
            >
              <Settings className="h-6 w-6 mb-2" />
              <span>HR Settings</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRDashboardPage;
